import React, { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ColumnDef,
    functionalUpdate,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Badge } from "@telia/teddy";
import { useSelector } from "react-redux";
import * as properNameCase from "../../../../../shared/properNameCase";
import { RootState } from "../../../../../models";
import { API_URL_GET_ORDERS_MISSING_SIGNING } from "../../api";
import { OrderStateEnum } from "../../types/enums";
import { OrderItem } from "../../types/models";
import { getStatusText, statusVariant } from "./orderStatusUtils";
import { getCommissionDeadlineStatus } from "../../utils";
import { getSalesAgents } from "../../state/selectors/selectors";

const PAGE_SIZE = 25;

const fetchOrders = async (
    fromDate?: Date | null,
    toDate?: Date | null,
    agentId = "",
    search = "",
): Promise<OrderItem[]> => {
    const url = new URL(API_URL_GET_ORDERS_MISSING_SIGNING, window.location.href);
    if (fromDate) url.searchParams.set("fromDate", fromDate.toISOString());
    if (toDate) url.searchParams.set("toDate", toDate.toISOString());
    if (agentId) url.searchParams.set("agentId", agentId);
    if (search) url.searchParams.set("search", search);
    const response = await fetch(url.toString());
    if (!response.ok) throw new Error("Kunne ikke hente ordre. Prøv igjen senere.");
    const result = await response.json();
    return result?.registrations ?? [];
};

const EMPTY_ORDERS: OrderItem[] = [];

interface UseOrdersMissingSigningTableOptions {
    agentId: string;
    enabled: boolean;
    endDate: Date | null;
    onPageIndexChange: (pageIndex: number) => void;
    onRowClickCb?: () => void;
    pageIndex: number;
    search: string;
    startDate: Date | null;
}

const useOrdersMissingSigningTable = ({
    agentId,
    enabled,
    endDate,
    onPageIndexChange,
    onRowClickCb,
    pageIndex,
    search,
    startDate,
}: UseOrdersMissingSigningTableOptions) => {
    const salesAgents = useSelector((state: RootState) => getSalesAgents(state));
    const queryClient = useQueryClient();
    const pagination = { pageIndex, pageSize: PAGE_SIZE };
    const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
    const [signedOrderIds, setSignedOrderIds] = useState(new Set<number>());

    const queryKey = ["ordersMissingSigning", startDate?.toISOString(), endDate?.toISOString(), agentId, search];

    const {
        data: orders = EMPTY_ORDERS,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey,
        queryFn: () => fetchOrders(startDate, endDate, agentId, search),
        enabled,
        retry: false,
    });

    const removeOrder = useCallback(
        (orderId: number) => {
            const key = ["ordersMissingSigning", startDate?.toISOString(), endDate?.toISOString(), agentId, search];
            setSignedOrderIds((prev) => new Set(prev).add(orderId));
            queryClient.setQueryData<OrderItem[]>(key, (old) => (old ? old.filter((o) => o.orderId !== orderId) : []));
            refetch();
        },
        [queryClient, refetch, startDate, endDate, agentId, search],
    );

    const filteredData = useMemo(() => {
        return signedOrderIds.size > 0 ? orders.filter((o) => !signedOrderIds.has(o.orderId)) : orders;
    }, [orders, signedOrderIds]);

    const toggleExpansion = (orderId: number) => {
        setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
    };

    const columns = useMemo<ColumnDef<OrderItem>[]>(
        () => [
            {
                accessorKey: "orderId",
                id: "ref",
                header: "Ordre-ID",
            },
            {
                accessorKey: "legalResponsibleName",
                id: "legalResponsibleName",
                header: "Kunde",
                cell: ({ getValue }) => <span data-di-mask>{properNameCase.convert(getValue<string>() ?? "")}</span>,
            },
            {
                accessorKey: "orderState",
                id: "orderState",
                header: "Status",
                cell: ({ getValue }) => {
                    const val = getValue<OrderStateEnum | undefined>();
                    if (!val) return "";
                    return (
                        <Badge variant={statusVariant(val)} hideIcon>
                            {getStatusText(val)}
                        </Badge>
                    );
                },
            },
            {
                accessorKey: "salesAgent",
                id: "salesAgent",
                header: "Selger",
                cell: ({ getValue }) => {
                    const salesAgentId = getValue<string | undefined>();
                    const salesAgent = salesAgents.find((agent) => String(agent.id) === String(salesAgentId));
                    return (
                        <span data-di-mask>
                            {salesAgent ? `${salesAgent.firstName} ${salesAgent.lastName.slice(0, 1)}.` : "Ikke angitt"}
                        </span>
                    );
                },
            },
            {
                accessorKey: "creationDate",
                header: "Opprettet",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    const d = new Date(val);
                    return isNaN(d.getTime())
                        ? val
                        : d.toLocaleString("nb-NO", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                          });
                },
            },
            {
                accessorKey: "creationDate",
                id: "commissionDeadline",
                header: "Frist",
                cell: ({ getValue }) => {
                    const val = getValue<string | undefined>();
                    if (!val) return "";
                    const status = getCommissionDeadlineStatus(val);
                    if (!status) return "";
                    return (
                        <Badge variant={status.variant} hideIcon>
                            {status.label}
                        </Badge>
                    );
                },
            },
        ],
        [salesAgents],
    );

    const table = useReactTable({
        columns,
        data: filteredData,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        autoResetPageIndex: false,
        state: { pagination },
        onPaginationChange: (updater) => onPageIndexChange(functionalUpdate(updater, pagination).pageIndex),
    });

    const onRowClick = (row: OrderItem) => {
        if (!row.orderId) {
            return;
        }
        const isCurrentlyExpanded = expandedOrderId === row.orderId;
        toggleExpansion(row.orderId);
        if (onRowClickCb && !isCurrentlyExpanded) {
            onRowClickCb();
        }
    };

    return {
        columns,
        error,
        expandedOrderId,
        isLoading,
        onRowClick,
        refetch,
        removeOrder,
        setExpandedOrderId,
        table,
    };
};

export default useOrdersMissingSigningTable;
